import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { TranslateModule } from '@ngx-translate/core';

import type { ShadowFeatureSettings, ShadowValues } from '@sites/entity-core';
import { DEFAULT_SHADOW_VALUES } from '@sites/entity-core';

import type { ColorPickerEventPayload } from '../../sp-color-picker/src/types/color-picker.type';
import { SpColorPickerComponent } from '../../sp-color-picker/src/sp-color-picker.component';
import { SpInputNumComponent } from '../../sp-form-controls/sp-input-num/sp-input-num.component';
import { pickedShadowColor, shadowColorAlpha, withShadowColorAlpha } from './shadow-color';

/**
 * Box-shadow editor bound to the versioned `shadow` feature's field — the shadow twin of
 * `sp-animation-settings-signal`. Widgets still on the legacy shape use `sp-shadow-settings-signal-legacy`.
 *
 * `settings` is `null` until the shadow is first edited, so the editor shows what the CSS draws in that
 * case (`DEFAULT_SHADOW_VALUES`) and writes `settings` whole rather than one leaf at a time.
 */
@Component({
    selector: 'sp-shadow-settings-signal',
    templateUrl: './sp-shadow-settings-signal.component.html',
    styleUrls: ['./sp-shadow-settings-signal.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, SpColorPickerComponent, SpInputNumComponent],
})
export class SpShadowSettingsSignalComponent {
    public readonly field = input.required<FieldTree<ShadowFeatureSettings>>();

    private readonly values = computed<ShadowValues>(() => this.field().settings().value() ?? DEFAULT_SHADOW_VALUES);

    public readonly offsetXValue = computed(() => this.values().offsetX);
    public readonly offsetYValue = computed(() => this.values().offsetY);
    public readonly blurValue = computed(() => this.values().blur);
    public readonly spreadValue = computed(() => this.values().spread);
    public readonly colorValue = computed(() => this.values().color);
    public readonly colorAlphaValue = computed(() => shadowColorAlpha(this.values().color));

    public setOffsetX(offsetX: number): void {
        this.patch({ offsetX });
    }

    public setOffsetY(offsetY: number): void {
        this.patch({ offsetY });
    }

    public setBlur(blur: number): void {
        this.patch({ blur });
    }

    public setSpread(spread: number): void {
        this.patch({ spread });
    }

    public setColorAlpha(alpha: number): void {
        this.patch({ color: withShadowColorAlpha(this.values().color, alpha) });
    }

    public setShadowColor({ value }: ColorPickerEventPayload): void {
        this.patch({ color: pickedShadowColor(value, this.colorAlphaValue()) });
    }

    private patch(patch: Partial<ShadowValues>): void {
        this.field()
            .settings()
            .value.set({ ...this.values(), ...patch });
    }
}
